import React, { useEffect, useState } from "react";
import { IHbDeal } from "../../shared/types/HbDeal.type";
import {
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  ListItemButton,
  Divider,
  Box,
  TextField,
  Button,
  Alert,
  Tooltip,
} from "@mui/material";
import { t } from "i18next";
import { useOnboarding } from "../../hooks/useOnboarding";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import toggleItemInArray from "../../shared/utils/arrays";
import { Icon } from "@iconify/react";
import palette from "../../theme/palette";
import { Info } from "@mui/icons-material";

interface WhatsappGroupConfigProps {
  hbData: IHbDeal | null;
  selectedDeals: string[];
  selectedArioneo: string[];
  groupName: string;
  setSelectedDeals: (val: string[]) => void;
  setSelectedArioneo: (val: string[]) => void;
  setGroupName: (val: string) => void;
  onGroupCreated: (uuid: string, channel: string) => void;
  creatingChannel: boolean;
  isUserHasAlreadyGroup?: boolean;
}

export const ARIONEO_SUUPORT_NUMBER = "+33629615328";

const WhatsappGroupConfig: React.FC<WhatsappGroupConfigProps> = ({
  creatingChannel,
  onGroupCreated,
  selectedArioneo,
  selectedDeals,
  groupName,
  setSelectedDeals,
  setSelectedArioneo,
  setGroupName,
  hbData,
  isUserHasAlreadyGroup = false,
}) => {
  const [customNumber, setCustomNumber] = useState("");
  const [dealPhones, setDealPhones] = useState<IHbDeal["deal_phones"]>([]);
  const { createGroup } = useOnboarding();

  useEffect(() => {
    setDealPhones(hbData?.deal_phones || []);
  }, [hbData?.deal_phones.length]);

  const handleDealChange = (phone: string) => {
    let phnes = toggleItemInArray(selectedDeals, phone);
    setSelectedDeals(phnes);
  };

  const handleToggleDealPhones = (phone: string) => {
    let deals = toggleItemInArray(dealPhones, {
      name: t("onboarding:manualEntry"),
      phone,
      whatsapp_valid: true,
    });
    setDealPhones(deals);
  };

  const handleArioneoChange = (phone: string) => {
    let prev = [...selectedArioneo];

    setSelectedArioneo(
      prev.includes(phone) ? prev.filter((p) => p !== phone) : [...prev, phone],
    );
  };
  const createWagroup = async () => {
    try {
      let response = await createGroup.mutateAsync({
        numbers: [...selectedDeals, ...selectedArioneo],
        groupName,
      });

      if (response.status == 200) {
        toast(t("onboarding:createWaGroupSuccess"), { type: "success" });
      }

      if (response.status == 202) {
        toast(t("onboarding:createWaGroupFailed"), { type: "error" });
      }
      onGroupCreated(response.data?.group_uuid, groupName);
    } catch (error) {
      toast(t("error:generic"), { type: "error" });
    }
  };

  const addCustomNumber = () => {
    handleDealChange(customNumber);
    handleToggleDealPhones(customNumber);
    setCustomNumber("");
  };

  return (
    <Grid container spacing={2}>
      {/* Deal numbers */}

      {isUserHasAlreadyGroup && (
        <Grid item md={12} xs={12}>
          <Alert variant="filled" severity="info">
            {t("onboarding:userHasGroups")}
          </Alert>
        </Grid>
      )}

      <Grid item md={6} sm={12} xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          {t("onboarding:dealNumber")}
        </Typography>
        <Box display={"flex"} gap={1} my={1}>
          <TextField
            value={customNumber}
            onChange={(e) => setCustomNumber(e.target.value)}
            fullWidth
            size="small"
            variant="outlined"
            label={t("onboarding:newPhone")}
          />
          <Button
            disabled={!customNumber}
            onClick={addCustomNumber}
            variant="contained"
            size="small"
          >
            {t("button:add")}
          </Button>
        </Box>
        <Box
          maxHeight={400}
          overflow="auto"
          border={1}
          borderColor="divider"
          borderRadius={1}
        >
          <List disablePadding>
            {dealPhones.map((deal, idx) => (
              <React.Fragment key={deal.phone}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDealChange(deal.phone);
                    }}
                  >
                    <Checkbox
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDealChange(deal.phone);
                      }}
                      edge="start"
                      checked={selectedDeals.includes(deal.phone)}
                      tabIndex={-1}
                      disableRipple
                    />
                    <ListItemText
                      primary={
                        <Box display={"flex"} alignItems={"center"} gap={1}>
                          <Typography variant="body2">{deal.name}</Typography>
                          {deal?.comment && (
                            <Tooltip arrow title={deal.comment} placement="right">
                              <Info sx={{ fontSize: "16px" }}></Info>
                            </Tooltip>
                          )}
                        </Box>
                      }
                      secondary={
                        <Box
                          display={"flex"}
                          flexDirection={{ md: "row", xs: "column" }}
                          flexWrap={"wrap"}
                          gap={1}
                          alignItems={{ md: "center", xs: "start" }}
                        >
                          <Typography variant="body2">{deal.phone}</Typography>
                          {!deal.whatsapp_valid && (
                            <Typography
                              variant="body2"
                              color={palette.light.warning.main}
                            >
                              <Icon icon={"fluent-emoji-flat:warning"} />
                              {t("waIncompatiblieNumber")}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {idx < dealPhones.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Grid>

      {/* Arioneo numbers */}
      <Grid item md={6} sm={12} xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          {t("onboarding:arioneoNumber")}
        </Typography>
        <Box
          maxHeight={400}
          overflow="auto"
          border={1}
          borderColor="divider"
          borderRadius={1}
        >
          <List disablePadding>
            {hbData?.arioneo_phones.map((arioneo, idx) => (
              <React.Fragment key={arioneo.phone}>
                <ListItem disablePadding>
                  <ListItemButton
                    disabled={arioneo.phone === ARIONEO_SUUPORT_NUMBER}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArioneoChange(arioneo.phone);
                    }}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedArioneo.includes(arioneo.phone)}
                      tabIndex={-1}
                      disableRipple
                      onClick={(e) => {
                        e.stopPropagation();
                        handleArioneoChange(arioneo.phone);
                      }}
                    />
                    <ListItemText
                      primary={
                        <Box display={"flex"} alignItems={"center"} gap={1}>
                          <Typography variant="body2">
                            {arioneo.name}
                          </Typography>
                          {arioneo?.comment && (
                            <Tooltip arrow title={arioneo.comment} placement="right">
                              <Info sx={{ fontSize: "16px" }}></Info>
                            </Tooltip>
                          )}
                        </Box>
                      }
                      secondary={arioneo.phone}
                    />
                  </ListItemButton>
                </ListItem>
                {idx < hbData.arioneo_phones.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Box>
      </Grid>
      <Grid item md={12} xs={12} spacing={2}>
        <Box display={"flex"} alignItems={"center"} gap={2}>
          <TextField
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            size="small"
            label={t("onboarding:groupNameField")}
          />
          <LoadingButton
            loading={createGroup.isLoading || creatingChannel}
            onClick={createWagroup}
            variant="contained"
          >
            {t("button:create")}
          </LoadingButton>
        </Box>
        <Alert severity="info" variant="standard" sx={{ mt: 2 }}>
          {t("adminRightWaGroup")}
        </Alert>
      </Grid>
    </Grid>
  );
};

export default WhatsappGroupConfig;
